/**
 * CharacterPortrait Component
 * 
 * Displays a character portrait with WebP/JPEG fallback support and lazy loading.
 * Uses the <picture> element for optimal format selection and provides fallback
 * display when portraits are not available.
 */

import React from 'react';
import { portraitService } from '../../services/portraitService';
import type { PortraitVariantSize } from '../../types/portrait';
import { usePictureImageLoad } from '../../hooks/usePictureImageLoad';
import PictureImageContent from './PictureImageContent';

export interface CharacterPortraitProps {
  /**
   * Portrait ID to display (e.g., 'adventurer_male', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  portraitId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 64x64px (for headers, small displays)
   * - card: 256x256px (for character cards, selection)
   * - large: 512x512px (for detail views, profiles)
   */
  variant?: PortraitVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  
  /**
   * Data test ID for testing
   */
  'data-testid'?: string;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;

  /**
   * When true, the portrait fills its container (width and height 100%).
   * Use in a fixed-size container (e.g. scene header) so portrait and banner share the same height.
   */
  fillContainer?: boolean;
}

/**
 * Default fallback component when portrait image cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: PortraitVariantSize; alt: string | undefined }> = ({ variant, alt }) => {
  const sizes = {
    thumbnail: 'w-16 h-16',
    card: 'w-64 h-64',
    large: 'w-96 h-96',
  };
  
  const iconSizes = {
    thumbnail: 'text-2xl',
    card: 'text-6xl',
    large: 'text-8xl',
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)'
      }}
      role="img"
      aria-label={alt || 'Character portrait placeholder'}
    >
      <span className={iconSizes[variant]}>👤</span>
    </div>
  );
};

/**
 * CharacterPortrait component
 */
export const CharacterPortrait: React.FC<CharacterPortraitProps> = ({
  portraitId,
  variant = 'card',
  alt,
  className = '',
  style,
  fallback,
  lazy = true,
  'data-testid': dataTestId,
  fillContainer = false,
}) => {
  const baseUrls = portraitService.getPortraitUrls(portraitId, variant);
  const identityKey = `${portraitId ?? 'generic'}-${variant}`;

  const {
    imgRef,
    isLoading,
    failedUrl,
    imageUrls,
    pictureKey,
    handleLoad,
    handleError,
  } = usePictureImageLoad(baseUrls, identityKey, {
    lazy,
    onLoadFailed: () => {
      console.warn(`Failed to load portrait: ${portraitId ?? 'generic'}`);
    },
  });

  if (!imageUrls) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }
  
  if (failedUrl === imageUrls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }

  const sizeClasses = {
    thumbnail: 'w-16 h-16',
    card: 'w-64 h-64',
    large: 'w-96 h-96',
  };
  const containerSizeClass = fillContainer ? 'w-full h-full' : sizeClasses[variant];
  const fillStyle = fillContainer ? { width: '100%', height: '100%', minHeight: 0 } : undefined;

  return (
    <div 
      className={`${containerSizeClass} relative ${className}`}
      data-testid={dataTestId}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...fillStyle,
        ...style
      }}
    >
      <PictureImageContent
        imageUrls={imageUrls}
        pictureKey={pictureKey}
        imgRef={imgRef}
        alt={alt || 'Character portrait'}
        lazy={lazy}
        isLoading={isLoading}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

export default CharacterPortrait;
